import { useState, useCallback } from 'react';
import { cameraService } from '@/services/camera/cameraService';
import type { CapturedImage } from '@/services/camera/cameraService';
import { PlatformUtils } from '@/utils/platform';

type CameraErrorCode = 'PERMISSION_DENIED' | 'CAMERA_ERROR' | 'USER_CANCELLED' | 'UNKNOWN';

type CameraError = {
  code: CameraErrorCode;
  message: string;
};

/**
 * React hook for camera operations
 * Provides methods for taking photos, picking from gallery, and handling errors
 */
export const useCamera = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CameraError | null>(null);

  const isNative = PlatformUtils.isNative();

  /**
   * Parse error message to determine error type
   */
  const parseError = (err: unknown): CameraError => {
    const message = err instanceof Error ? err.message : String(err);

    // Capacitor camera returns "User cancelled photos app" when cancelled
    if (message.toLowerCase().includes('cancel')) {
      return { code: 'USER_CANCELLED', message: 'Photo capture cancelled' };
    }

    if (message.toLowerCase().includes('permission')) {
      return { code: 'PERMISSION_DENIED', message: 'Camera permission denied' };
    }

    return { code: 'CAMERA_ERROR', message: message || 'Failed to capture photo' };
  };

  /**
   * Take a photo using the device camera
   */
  const takePhoto = useCallback(async (): Promise<CapturedImage | null> => {
    setLoading(true);
    setError(null);

    try {
      const hasPermission = await cameraService.requestPermissions();
      if (!hasPermission) {
        setError({ code: 'PERMISSION_DENIED', message: 'Camera permission denied' });
        return null;
      }

      const image = await cameraService.takePhoto({ quality: 90 });
      return image;
    } catch (err: unknown) {
      const cameraError = parseError(err);
      // Don't set error state for user cancellation
      if (cameraError.code !== 'USER_CANCELLED') {
        setError(cameraError);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Select a photo from the device gallery
   */
  const pickFromGallery = useCallback(async (): Promise<CapturedImage | null> => {
    setLoading(true);
    setError(null);

    try {
      const image = await cameraService.pickFromGallery({ quality: 90 });
      return image;
    } catch (err: unknown) {
      const cameraError = parseError(err);
      // Don't set error state for user cancellation
      if (cameraError.code !== 'USER_CANCELLED') {
        setError(cameraError);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Show native prompt to choose camera or gallery
   */
  const promptForImage = useCallback(async (): Promise<CapturedImage | null> => {
    setLoading(true);
    setError(null);

    try {
      const image = await cameraService.promptForImage({ quality: 90 });
      return image;
    } catch (err: unknown) {
      const cameraError = parseError(err);
      // Don't set error state for user cancellation
      if (cameraError.code !== 'USER_CANCELLED') {
        setError(cameraError);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => setError(null), []);

  return {
    /** Whether running on native platform (Android/iOS) */
    isNative,
    /** Whether a camera operation is in progress */
    loading,
    /** Current error state, if any */
    error,
    /** Take a photo with device camera */
    takePhoto,
    /** Select from device gallery */
    pickFromGallery,
    /** Show native picker (camera/gallery choice) */
    promptForImage,
    /** Clear error state */
    clearError,
  };
};
